import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Receipt, CreditCard, Building2, Wallet, Clock, CheckCircle, XCircle,
  AlertTriangle, Download, Eye, Loader2, Plus, Globe, Server, Wrench,
  FileText, Euro, Calendar, ExternalLink, Search, Filter, RefreshCw,
  Play, Pause, StopCircle, RotateCcw, Copy, CheckCheck
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_placeholder');

// Stripe Card Form Component
const StripeCardForm = ({ clientSecret, onSuccess, onError, amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (error) {
        onError(error.message);
      } else if (paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent);
      }
    } catch (err) {
      onError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border rounded-lg">
        <CardElement options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': { color: '#aab7c4' },
            },
          },
        }} />
      </div>
      <Button type="submit" disabled={!stripe || processing} className="w-full gap-2">
        {processing ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Traitement...</>
        ) : (
          <><CreditCard className="w-4 h-4" /> Payer {amount?.toFixed(2)}€</>
        )}
      </Button>
    </form>
  );
};

const ServiceInvoicesPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const isAgency = user?.role === 'agency' || user?.role === 'project_manager';
  
  // Data
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [domains, setDomains] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Create invoice modal (agency)
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    client_id: '',
    service_type: 'domain',
    service_name: '',
    description: '',
    amount: '',
    tax_rate: 20,
    is_recurring: false,
    recurring_frequency: 'yearly',
    recurring_start_date: '',
    recurring_end_date: '',
    linked_domain_id: '',
    due_date: '',
    auto_debit_wallet: false,
    allow_wallet_payment: true,
    allow_card_payment: true,
    allow_bank_transfer: true
  });
  
  // Payment modal (client)
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [processing, setProcessing] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  
  // Receipt upload modal (agency)
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState('');
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  
  // Recurring management
  const [stoppingRecurring, setStoppingRecurring] = useState(false);
  
  // Copy to clipboard state
  const [copiedField, setCopiedField] = useState(null);
  
  // Copy to clipboard helper
  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success('Copié dans le presse-papiers !');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Erreur lors de la copie');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [invoicesRes, walletRes] = await Promise.all([
        api.get('/service-invoices/'),
        api.get('/wallet/').catch(() => ({ data: null }))
      ]);
      
      setInvoices(invoicesRes.data || []);
      setWallet(walletRes.data);
      
      if (isAgency) {
        const [clientsRes, domainsRes] = await Promise.all([
          api.get('/users/?role=client'),
          api.get('/domains/')
        ]);
        setClients(clientsRes.data || []);
        setDomains(domainsRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [token, isAgency]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateInvoice = async () => {
    if (!newInvoice.client_id || !newInvoice.service_name || !newInvoice.amount) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (newInvoice.is_recurring && !newInvoice.recurring_start_date) {
      toast.error('Veuillez renseigner la date de début pour la facturation récurrente');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API}/service-invoices/`, {
        ...newInvoice,
        amount: parseFloat(newInvoice.amount),
        due_date: newInvoice.due_date ? new Date(newInvoice.due_date).toISOString() : null,
        recurring_start_date: newInvoice.recurring_start_date ? new Date(newInvoice.recurring_start_date).toISOString() : null,
        recurring_end_date: newInvoice.recurring_end_date ? new Date(newInvoice.recurring_end_date).toISOString() : null
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Demande de règlement envoyée au client');
      setShowCreateModal(false);
      resetCreateForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const resetCreateForm = () => {
    setNewInvoice({
      client_id: '',
      service_type: 'domain',
      service_name: '',
      description: '',
      amount: '',
      tax_rate: 20,
      is_recurring: false,
      recurring_frequency: 'yearly',
      recurring_start_date: '',
      recurring_end_date: '',
      linked_domain_id: '',
      due_date: '',
      auto_debit_wallet: false,
      allow_wallet_payment: true,
      allow_card_payment: true,
      allow_bank_transfer: true
    });
  };

  const handleStopRecurring = async (invoice) => {
    if (!window.confirm(`Voulez-vous vraiment arrêter le renouvellement automatique pour "${invoice.service_name}" ?`)) {
      return;
    }
    
    setStoppingRecurring(true);
    try {
      await axios.post(
        `${API}/service-invoices/${invoice.id}/stop-recurring`,
        { reason: 'Arrêté manuellement' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Renouvellement arrêté');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'arrêt du renouvellement');
    } finally {
      setStoppingRecurring(false);
    }
  };

  const handleResumeRecurring = async (invoice) => {
    setStoppingRecurring(true);
    try {
      await axios.post(
        `${API}/service-invoices/${invoice.id}/resume-recurring`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Renouvellement repris');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la reprise');
    } finally {
      setStoppingRecurring(false);
    }
  };

  const openPaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentMethod('');
    setStripeClientSecret(null);
    setBankDetails(null);
    setShowPaymentModal(true);
  };

  const handlePayment = async () => {
    if (!paymentMethod || !selectedInvoice) return;
    
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/service-invoices/${selectedInvoice.id}/pay`,
        { payment_method: paymentMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (paymentMethod === 'stripe') {
        setStripeClientSecret(response.data.client_secret);
      } else if (paymentMethod === 'bank_transfer') {
        setBankDetails(response.data.bank_details);
        toast.info('Instructions de virement affichées');
      } else if (paymentMethod === 'wallet') {
        toast.success('Paiement effectué avec succès');
        setShowPaymentModal(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setProcessing(false);
    }
  };

  const handleStripeSuccess = async () => {
    toast.success('Paiement effectué avec succès');
    
    // Confirm payment on backend
    try {
      await axios.post(
        `${API}/service-invoices/${selectedInvoice.id}/confirm-payment`,
        { payment_reference: `STRIPE-${Date.now()}` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      // Payment already processed by webhook typically
    }
    
    setShowPaymentModal(false);
    fetchData();
  };

  const handleUploadReceipt = async () => {
    if (!receiptUrl || !selectedInvoice) return;
    
    setUploadingReceipt(true);
    try {
      await axios.post(
        `${API}/service-invoices/${selectedInvoice.id}/upload-receipt`,
        { receipt_url: receiptUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reçu déposé avec succès');
      setShowReceiptModal(false);
      setReceiptUrl('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du dépôt du reçu');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const confirmBankPayment = async (invoice) => {
    const reference = prompt('Entrez la référence du virement :');
    if (!reference) return;
    
    try {
      await axios.post(
        `${API}/service-invoices/${invoice.id}/confirm-payment`,
        { payment_reference: reference },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Paiement confirmé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la confirmation');
    }
  };

  const openReceiptModal = (invoice) => {
    setSelectedInvoice(invoice);
    setReceiptUrl(invoice.receipt_url || '');
    setShowReceiptModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-amber-100 text-amber-700', icon: Clock, label: 'En attente' },
      paid: { bg: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Payé' },
      cancelled: { bg: 'bg-slate-100 text-slate-700', icon: XCircle, label: 'Annulé' },
      overdue: { bg: 'bg-red-100 text-red-700', icon: AlertTriangle, label: 'En retard' }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <Badge className={cn('gap-1', style.bg)}>
        <Icon className="w-3 h-3" />
        {style.label}
      </Badge>
    );
  };

  const getServiceIcon = (type) => {
    const icons = {
      domain: Globe,
      hosting: Server,
      maintenance: Wrench,
      other: FileText
    };
    return icons[type] || FileText;
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = inv.service_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          inv.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || inv.status === activeTab;
    return matchesSearch && matchesTab;
  });

  const stats = {
    pending: invoices.filter(i => i.status === 'pending').length,
    pendingAmount: invoices.filter(i => i.status === 'pending').reduce((sum, i) => sum + i.total_amount, 0),
    paid: invoices.filter(i => i.status === 'paid').length,
    paidAmount: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.total_amount, 0)
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn("transition-all duration-300", collapsed ? "md:ml-20" : "md:ml-64")}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="p-4 md:p-8" style={{ paddingTop: '140px' }}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                {isAgency ? 'Facturation Services' : 'Mes Règlements'}
              </h1>
              <p className="text-slate-500 mt-1">
                {isAgency 
                  ? 'Gérez les demandes de règlement aux clients pour les services (hébergement, nom de domaine, maintenance...)'
                  : 'Consultez et payez vos factures de services (hébergement, nom de domaine, maintenance...)'}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                💡 Les factures liées aux projets sont disponibles dans chaque projet
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {!isAgency && wallet && (
                <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-xl">
                  <Wallet className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="text-xs text-emerald-600">Solde Wallet</p>
                    <p className="font-bold text-emerald-700">{wallet.balance_amount?.toFixed(2) || '0.00'}€</p>
                  </div>
                </div>
              )}
              
              {isAgency && (
                <Button onClick={() => setShowCreateModal(true)} style={{ backgroundColor: theme.colors?.accent }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle demande
                </Button>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.pending}</p>
                    <p className="text-xs text-slate-500">En attente</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Euro className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.pendingAmount.toFixed(0)}€</p>
                    <p className="text-xs text-slate-500">À régler</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.paid}</p>
                    <p className="text-xs text-slate-500">Payées</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Euro className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.paidAmount.toFixed(0)}€</p>
                    <p className="text-xs text-slate-500">Total payé</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">Tous</TabsTrigger>
                <TabsTrigger value="pending">En attente</TabsTrigger>
                <TabsTrigger value="paid">Payés</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Invoices List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredInvoices.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucune facture</h3>
                <p className="text-slate-500">
                  {isAgency ? 'Créez une nouvelle demande de règlement' : 'Vous n\'avez aucune facture pour le moment'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredInvoices.map(invoice => {
                const ServiceIcon = getServiceIcon(invoice.service_type);
                const frequencyLabel = {
                  monthly: 'Mensuel',
                  quarterly: 'Trimestriel',
                  yearly: 'Annuel'
                };
                return (
                  <Card key={invoice.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        {/* Left: Invoice Info */}
                        <div className="flex items-start gap-4 flex-1">
                          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                            <ServiceIcon className="w-6 h-6 text-slate-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold">{invoice.service_name}</h3>
                              {getStatusBadge(invoice.status)}
                              {invoice.is_recurring && (
                                <Badge 
                                  variant="outline" 
                                  className={cn(
                                    "gap-1",
                                    invoice.recurring_stopped ? "border-red-200 text-red-600" : "border-blue-200 text-blue-600"
                                  )}
                                >
                                  <RefreshCw className="w-3 h-3" />
                                  {invoice.recurring_stopped ? 'Arrêté' : frequencyLabel[invoice.recurring_frequency] || 'Récurrent'}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 mt-1">
                              N° {invoice.invoice_number}
                              {isAgency && invoice.client_name && ` • ${invoice.client_name}`}
                            </p>
                            {invoice.description && (
                              <p className="text-sm text-slate-600 mt-1">{invoice.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
                              </span>
                              {invoice.due_date && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Échéance: {new Date(invoice.due_date).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                              {invoice.is_recurring && !invoice.recurring_stopped && invoice.next_due_date && (
                                <span className="flex items-center gap-1 text-blue-600">
                                  <RefreshCw className="w-3 h-3" />
                                  Prochain: {new Date(invoice.next_due_date).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                              {invoice.is_recurring && (
                                <span className="flex items-center gap-1">
                                  Fin: {invoice.recurring_end_date ? new Date(invoice.recurring_end_date).toLocaleDateString('fr-FR') : 'Indéterminée'}
                                </span>
                              )}
                              {invoice.paid_via && (
                                <span className="flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3 text-emerald-500" />
                                  Payé par {invoice.paid_via === 'wallet' ? 'Wallet' : invoice.paid_via === 'stripe' ? 'CB' : 'Virement'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Right: Amount & Actions */}
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Montant TTC</p>
                            <p className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                              {invoice.total_amount?.toFixed(2)}€
                            </p>
                            <p className="text-xs text-slate-400">
                              ({invoice.amount?.toFixed(2)}€ HT + {invoice.tax_amount?.toFixed(2)}€ TVA)
                            </p>
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            {/* Client actions */}
                            {!isAgency && invoice.status === 'pending' && (
                              <Button
                                size="sm"
                                onClick={() => openPaymentModal(invoice)}
                                style={{ backgroundColor: theme.colors?.accent }}
                              >
                                <CreditCard className="w-4 h-4 mr-1" />
                                Payer
                              </Button>
                            )}
                            
                            {/* Agency actions */}
                            {isAgency && invoice.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => confirmBankPayment(invoice)}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Confirmer paiement
                              </Button>
                            )}
                            
                            {isAgency && invoice.status === 'paid' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openReceiptModal(invoice)}
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                {invoice.receipt_url ? 'Voir reçu' : 'Déposer reçu'}
                              </Button>
                            )}
                            
                            {/* Recurring management */}
                            {isAgency && invoice.is_recurring && !invoice.recurring_stopped && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleStopRecurring(invoice)}
                                disabled={stoppingRecurring}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <StopCircle className="w-4 h-4 mr-1" />
                                Arrêter
                              </Button>
                            )}
                            
                            {isAgency && invoice.is_recurring && invoice.recurring_stopped && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleResumeRecurring(invoice)}
                                disabled={stoppingRecurring}
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              >
                                <Play className="w-4 h-4 mr-1" />
                                Reprendre
                              </Button>
                            )}
                            
                            {invoice.receipt_url && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => window.open(invoice.receipt_url, '_blank')}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Télécharger
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Create Invoice Modal (Agency) */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Nouvelle demande de règlement
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select value={newInvoice.client_id} onValueChange={(v) => setNewInvoice({...newInvoice, client_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.company_name || `${c.first_name} ${c.last_name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Type de service</Label>
              <Select value={newInvoice.service_type} onValueChange={(v) => setNewInvoice({...newInvoice, service_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="domain">Nom de domaine</SelectItem>
                  <SelectItem value="hosting">Hébergement</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {newInvoice.service_type === 'domain' && domains.filter(d => d.client_id === newInvoice.client_id).length > 0 && (
              <div className="space-y-2">
                <Label>Domaine associé</Label>
                <Select value={newInvoice.linked_domain_id} onValueChange={(v) => {
                  const domain = domains.find(d => d.id === v);
                  setNewInvoice({
                    ...newInvoice, 
                    linked_domain_id: v,
                    service_name: domain ? `Renouvellement ${domain.domain_name}` : newInvoice.service_name,
                    amount: domain?.annual_cost || newInvoice.amount
                  });
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un domaine" />
                  </SelectTrigger>
                  <SelectContent>
                    {domains.filter(d => d.client_id === newInvoice.client_id).map(d => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.domain_name} ({d.annual_cost}€/an)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Intitulé du service *</Label>
              <Input 
                value={newInvoice.service_name}
                onChange={(e) => setNewInvoice({...newInvoice, service_name: e.target.value})}
                placeholder="Ex: Renouvellement domaine exemple.fr"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={newInvoice.description}
                onChange={(e) => setNewInvoice({...newInvoice, description: e.target.value})}
                placeholder="Détails du service..."
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Montant HT (€) *</Label>
                <Input 
                  type="number"
                  value={newInvoice.amount}
                  onChange={(e) => setNewInvoice({...newInvoice, amount: e.target.value})}
                  placeholder="Ex: 15"
                />
              </div>
              <div className="space-y-2">
                <Label>TVA (%)</Label>
                <Input 
                  type="number"
                  value={newInvoice.tax_rate}
                  onChange={(e) => setNewInvoice({...newInvoice, tax_rate: parseFloat(e.target.value) || 20})}
                />
              </div>
            </div>
            
            {newInvoice.amount && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm">
                  <span className="text-slate-500">Montant TTC : </span>
                  <span className="font-bold">{(parseFloat(newInvoice.amount) * (1 + newInvoice.tax_rate/100)).toFixed(2)}€</span>
                </p>
              </div>
            )}
            
            {!newInvoice.is_recurring && (
              <div className="space-y-2">
                <Label>Date d&apos;échéance</Label>
                <Input 
                  type="date"
                  value={newInvoice.due_date}
                  onChange={(e) => setNewInvoice({...newInvoice, due_date: e.target.value})}
                />
              </div>
            )}
            
            {/* Recurring billing section */}
            <div className="space-y-3 p-4 border rounded-lg bg-slate-50">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_recurring"
                  checked={newInvoice.is_recurring}
                  onChange={(e) => setNewInvoice({...newInvoice, is_recurring: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="is_recurring" className="cursor-pointer font-medium">
                  <RefreshCw className="w-4 h-4 inline mr-1" />
                  Facturation récurrente (renouvellement automatique)
                </Label>
              </div>
              
              {newInvoice.is_recurring && (
                <div className="space-y-3 pt-3 border-t">
                  <div className="space-y-2">
                    <Label>Fréquence *</Label>
                    <Select 
                      value={newInvoice.recurring_frequency} 
                      onValueChange={(v) => setNewInvoice({...newInvoice, recurring_frequency: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Mensuel (tous les mois)</SelectItem>
                        <SelectItem value="quarterly">Trimestriel (tous les 3 mois)</SelectItem>
                        <SelectItem value="yearly">Annuel (tous les 12 mois)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date de début *</Label>
                      <Input 
                        type="date"
                        value={newInvoice.recurring_start_date}
                        onChange={(e) => setNewInvoice({...newInvoice, recurring_start_date: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date de fin</Label>
                      <Input 
                        type="date"
                        value={newInvoice.recurring_end_date}
                        onChange={(e) => setNewInvoice({...newInvoice, recurring_end_date: e.target.value})}
                        placeholder="Laisser vide = indéterminée"
                      />
                      <p className="text-xs text-slate-500">
                        {newInvoice.recurring_end_date ? '' : 'Laisser vide pour une durée indéterminée'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="auto_debit_wallet"
                      checked={newInvoice.auto_debit_wallet}
                      onChange={(e) => setNewInvoice({...newInvoice, auto_debit_wallet: e.target.checked})}
                      className="rounded"
                    />
                    <Label htmlFor="auto_debit_wallet" className="cursor-pointer text-sm">
                      <Wallet className="w-4 h-4 inline mr-1" />
                      Débit automatique du wallet si solde suffisant
                    </Label>
                  </div>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Modes de paiement autorisés</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="allow_wallet"
                    checked={newInvoice.allow_wallet_payment}
                    onChange={(e) => setNewInvoice({...newInvoice, allow_wallet_payment: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="allow_wallet" className="cursor-pointer flex items-center gap-1">
                    <Wallet className="w-4 h-4" /> Wallet
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="allow_card"
                    checked={newInvoice.allow_card_payment}
                    onChange={(e) => setNewInvoice({...newInvoice, allow_card_payment: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="allow_card" className="cursor-pointer flex items-center gap-1">
                    <CreditCard className="w-4 h-4" /> Carte bancaire
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="allow_bank"
                    checked={newInvoice.allow_bank_transfer}
                    onChange={(e) => setNewInvoice({...newInvoice, allow_bank_transfer: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="allow_bank" className="cursor-pointer flex items-center gap-1">
                    <Building2 className="w-4 h-4" /> Virement bancaire
                  </Label>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
            <Button onClick={handleCreateInvoice} disabled={creating} style={{ backgroundColor: theme.colors?.accent }}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Envoyer au client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Modal (Client) */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payer la facture
            </DialogTitle>
            <DialogDescription>
              {selectedInvoice?.service_name} - {selectedInvoice?.total_amount?.toFixed(2)}€
            </DialogDescription>
          </DialogHeader>
          
          {!stripeClientSecret && !bankDetails && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">Choisissez votre mode de paiement :</p>
              
              <div className="space-y-3">
                {selectedInvoice?.allow_wallet_payment && wallet && (
                  <button
                    onClick={() => setPaymentMethod('wallet')}
                    className={cn(
                      "w-full p-4 border rounded-xl flex items-center gap-4 transition-colors",
                      paymentMethod === 'wallet' ? "border-emerald-500 bg-emerald-50" : "hover:bg-slate-50"
                    )}
                  >
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Wallet className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold">Wallet</p>
                      <p className="text-sm text-slate-500">Solde: {wallet.balance_amount?.toFixed(2) || '0.00'}€</p>
                    </div>
                    {wallet.balance_amount >= selectedInvoice?.total_amount ? (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <span className="text-xs text-red-500">Solde insuffisant</span>
                    )}
                  </button>
                )}
                
                {selectedInvoice?.allow_card_payment && (
                  <button
                    onClick={() => setPaymentMethod('stripe')}
                    className={cn(
                      "w-full p-4 border rounded-xl flex items-center gap-4 transition-colors",
                      paymentMethod === 'stripe' ? "border-blue-500 bg-blue-50" : "hover:bg-slate-50"
                    )}
                  >
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold">Carte bancaire</p>
                      <p className="text-sm text-slate-500">Visa, Mastercard, etc.</p>
                    </div>
                  </button>
                )}
                
                {selectedInvoice?.allow_bank_transfer && (
                  <button
                    onClick={() => setPaymentMethod('bank_transfer')}
                    className={cn(
                      "w-full p-4 border rounded-xl flex items-center gap-4 transition-colors",
                      paymentMethod === 'bank_transfer' ? "border-slate-500 bg-slate-50" : "hover:bg-slate-50"
                    )}
                  >
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-slate-600" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold">Virement bancaire</p>
                      <p className="text-sm text-slate-500">Délai de traitement : 2-3 jours</p>
                    </div>
                  </button>
                )}
              </div>
              
              <Button 
                onClick={handlePayment} 
                disabled={!paymentMethod || processing || (paymentMethod === 'wallet' && wallet?.balance_amount < selectedInvoice?.total_amount)}
                className="w-full"
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {paymentMethod === 'wallet' ? 'Payer avec le Wallet' : 
                 paymentMethod === 'stripe' ? 'Continuer vers le paiement' :
                 paymentMethod === 'bank_transfer' ? 'Afficher les coordonnées' : 'Sélectionnez un mode'}
              </Button>
            </div>
          )}
          
          {stripeClientSecret && (
            <div className="py-4">
              <Elements stripe={stripePromise}>
                <StripeCardForm 
                  clientSecret={stripeClientSecret}
                  amount={selectedInvoice?.total_amount}
                  onSuccess={handleStripeSuccess}
                  onError={(msg) => toast.error(msg)}
                />
              </Elements>
            </div>
          )}
          
          {bankDetails && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Coordonnées bancaires
                </h4>
                
                {/* Titulaire */}
                {bankDetails.holder && (
                  <div className="flex items-center justify-between p-2 bg-white rounded-lg border">
                    <div>
                      <p className="text-xs text-slate-500">Titulaire</p>
                      <p className="font-medium">{bankDetails.holder}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(bankDetails.holder, 'holder')}
                      className="h-8 w-8 p-0"
                    >
                      {copiedField === 'holder' ? (
                        <CheckCheck className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                )}
                
                {/* Banque */}
                {bankDetails.bank_name && (
                  <div className="flex items-center justify-between p-2 bg-white rounded-lg border">
                    <div>
                      <p className="text-xs text-slate-500">Banque</p>
                      <p className="font-medium">{bankDetails.bank_name}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(bankDetails.bank_name, 'bank_name')}
                      className="h-8 w-8 p-0"
                    >
                      {copiedField === 'bank_name' ? (
                        <CheckCheck className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                )}
                
                {/* IBAN */}
                <div className="flex items-center justify-between p-2 bg-white rounded-lg border">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-500">IBAN</p>
                    <p className="font-mono font-medium text-sm break-all">
                      {bankDetails.iban || 'Non renseigné'}
                    </p>
                  </div>
                  {bankDetails.iban && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(bankDetails.iban, 'iban')}
                      className="h-8 w-8 p-0 ml-2 shrink-0"
                    >
                      {copiedField === 'iban' ? (
                        <CheckCheck className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
                
                {/* BIC */}
                <div className="flex items-center justify-between p-2 bg-white rounded-lg border">
                  <div>
                    <p className="text-xs text-slate-500">BIC / SWIFT</p>
                    <p className="font-mono font-medium">{bankDetails.bic || 'Non renseigné'}</p>
                  </div>
                  {bankDetails.bic && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(bankDetails.bic, 'bic')}
                      className="h-8 w-8 p-0"
                    >
                      {copiedField === 'bic' ? (
                        <CheckCheck className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
                
                {/* Référence du virement */}
                <div className="flex items-center justify-between p-2 bg-amber-50 rounded-lg border border-amber-200">
                  <div>
                    <p className="text-xs text-amber-600">Référence à indiquer</p>
                    <p className="font-mono font-bold text-amber-800">{selectedInvoice?.invoice_number}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(selectedInvoice?.invoice_number, 'reference')}
                    className="h-8 w-8 p-0"
                  >
                    {copiedField === 'reference' ? (
                      <CheckCheck className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                <strong>Important :</strong> Indiquez la référence <strong>{selectedInvoice?.invoice_number}</strong> dans le motif de votre virement pour faciliter le traitement.
              </div>
              
              <Button variant="outline" onClick={() => setShowPaymentModal(false)} className="w-full">
                Fermer
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Upload Modal (Agency) */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Déposer le reçu
            </DialogTitle>
            <DialogDescription>
              Uploadez la facture PDF pour {selectedInvoice?.service_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>URL du fichier (PDF)</Label>
              <Input 
                value={receiptUrl}
                onChange={(e) => setReceiptUrl(e.target.value)}
                placeholder="https://example.com/facture.pdf"
              />
              <p className="text-xs text-slate-500">
                Collez l&apos;URL du fichier PDF uploadé sur votre serveur ou service de stockage
              </p>
            </div>
            
            {selectedInvoice?.receipt_url && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">Reçu actuel :</p>
                <a href={selectedInvoice.receipt_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />
                  {selectedInvoice.receipt_url.substring(0, 50)}...
                </a>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiptModal(false)}>Annuler</Button>
            <Button onClick={handleUploadReceipt} disabled={uploadingReceipt || !receiptUrl} style={{ backgroundColor: theme.colors?.accent }}>
              {uploadingReceipt ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Déposer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceInvoicesPage;
